import { type ComputedRef, nextTick, type Ref, watch } from 'vue'

import { useAiAssistantStore } from '@/stores/aiAssistant'

import {
  readOnboardingEnterDataMode,
  writeOnboardingEnterDataMode,
} from '../lib/onboardingProgressStorage'

/**
 * US accounts start the enter-data step on the manual form; the AI chat is
 * opt-in via "Switch to AI onboarding". The user's explicit choice (the mode
 * switch handlers below) is remembered per company so a reload keeps it.
 */
export function useOnboardingEnterDataModePreference(options: {
  companyId: Ref<string | null | undefined> | ComputedRef<string | null | undefined>
  isUsOnboarding: Ref<boolean> | ComputedRef<boolean>
  syncAiContext: () => void
  initAssistantChat: () => unknown
}) {
  const { companyId, isUsOnboarding, syncAiContext, initAssistantChat } = options

  // `sync` so the flag is set before the restored step (applied when the company
  // loads) bootstraps the enter-data step and would otherwise start the chat.
  watch(
    isUsOnboarding,
    (isUs) => {
      if (!isUs) return
      if (readOnboardingEnterDataMode(companyId.value) === 'chat') return
      useAiAssistantStore().switchToManualOnboarding()
    },
    { immediate: true, flush: 'sync' }
  )

  function switchToManualOnboarding() {
    useAiAssistantStore().switchToManualOnboarding()
    writeOnboardingEnterDataMode(companyId.value, 'form')
    syncAiContext()
  }

  function switchToAssistantOnboarding() {
    const aiStore = useAiAssistantStore()
    aiStore.switchToAssistantOnboarding()
    writeOnboardingEnterDataMode(companyId.value, 'chat')
    syncAiContext()
    nextTick(() => {
      aiStore.open()
      void initAssistantChat()
    })
  }

  return { switchToManualOnboarding, switchToAssistantOnboarding }
}
